// src/pages/client-360-report/index.jsx
// Reporte Cliente 360°: filtro por cliente + período, vista maestro-detalle.
// Datos: v_client_report_master (1 fila por oferta) + v_client_report_detail (1 fila por instrumento).

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../utils/supabase";

/* --- Constantes --- */
const STATUS_LABEL = {
  draft: "Borrador",
  sent: "Enviada",
  accepted: "Aceptada",
  billing: "Facturando",
  closed: "Cerrada",
  rejected: "Rechazada",
};

const STATUS_PILL = {
  draft: "bg-zinc-500/20 text-zinc-300",
  sent: "bg-sky-500/20 text-sky-300",
  accepted: "bg-emerald-500/20 text-emerald-300",
  billing: "bg-amber-500/20 text-amber-300",
  closed: "bg-violet-500/20 text-violet-300",
  rejected: "bg-rose-500/20 text-rose-300",
};

const TECH_STATUS_PILL = {
  Entregado: "bg-emerald-500/20 text-emerald-300",
  Calibrado: "bg-sky-500/20 text-sky-300",
  "En proceso": "bg-amber-500/20 text-amber-300",
  Pendiente: "bg-zinc-500/20 text-zinc-300",
};

/* --- Helpers --- */
const fmt = (n, dec = 0) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

const currencySymbol = (cur) => {
  if (!cur) return "$";
  const c = cur.toUpperCase();
  if (c.includes("USD") || c.includes("U$D")) return "U$D ";
  if (c === "EUR") return "€";
  return "$";
};

const fmtMoney = (n, cur) => `${currencySymbol(cur)}${fmt(n, 2)}`;

const normalizeCurrency = (cur) => {
  const c = String(cur || "ARS").toUpperCase();
  if (c.includes("USD") || c.includes("U$D")) return "USD";
  if (c.includes("EUR")) return "EUR";
  return "ARS";
};

const currencyLabel = (cur) => {
  if (cur === "USD") return "Dólares";
  if (cur === "EUR") return "Euros";
  return "Pesos";
};

const getCurrencyRows = (values = {}) => {
  const totals = {};
  for (const [cur, value] of Object.entries(values || {})) {
    const key = normalizeCurrency(cur);
    totals[key] = (totals[key] || 0) + Number(value || 0);
  }

  return ["ARS", "USD", "EUR"]
    .filter((key) => Object.prototype.hasOwnProperty.call(totals, key))
    .map((key) => ({ key, label: currencyLabel(key), value: totals[key] }))
    .filter((row) => row.value !== 0);
};

function CurrencyBreakdown({ values, compact = false }) {
  const rows = getCurrencyRows(values);
  if (!rows.length) return "—";

  return (
    <div className={compact ? "flex flex-col gap-0.5" : "flex flex-col gap-1.5"}>
      {rows.map((row) => (
        <div
          key={row.key}
          className={compact ? "leading-tight" : "rounded-lg border border-white/10 bg-white/5 px-3 py-2"}
        >
          <div
            className={
              compact
                ? "text-[10px] font-semibold uppercase tracking-wide text-muted"
                : "text-[11px] font-semibold uppercase tracking-wide text-muted"
            }
          >
            {row.label}
          </div>
          <div
            className={
              compact
                ? "font-mono text-xs font-semibold text-foreground"
                : "font-mono text-xl font-bold text-foreground"
            }
          >
            {fmtMoney(row.value, row.key)}
          </div>
        </div>
      ))}
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR");
};

// Devuelve {from, to} ISO según el periodo seleccionado
function computePeriodRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === "custom") {
    return { from: customFrom || null, to: customTo || null };
  }
  if (period === "week") {
    // semana ISO actual (lun-dom)
    const day = now.getDay() || 7; // 1..7, lun=1
    const monday = new Date(now);
    monday.setDate(now.getDate() - day + 1);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { from: monday.toISOString(), to: sunday.toISOString() };
  }
  if (period === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { from: first.toISOString(), to: last.toISOString() };
  }
  if (period === "year") {
    const first = new Date(now.getFullYear(), 0, 1);
    const last = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { from: first.toISOString(), to: last.toISOString() };
  }
  return { from: null, to: null };
}

/* --- Selector de cliente con búsqueda --- */
function ClientPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Carga clientes que matchean la búsqueda (debounced)
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (!open) return;
      setLoading(true);
      const q = supabase
        .from("v_client_report_master")
        .select("client_id, client_name")
        .not("client_id", "is", null)
        .limit(50);
      if (query.trim()) q.ilike("client_name", `%${query.trim()}%`);
      const { data } = await q;
      if (!active) return;
      // dedupe por client_id
      const seen = new Set();
      const uniq = (data || []).filter((r) => {
        if (seen.has(r.client_id)) return false;
        seen.add(r.client_id);
        return true;
      });
      uniq.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
      setOptions(uniq);
      setLoading(false);
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, open]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value ? value.client_name : query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Buscar cliente…"
        className="w-72 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-sky-400"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
          title="Limpiar"
        >
          ×
        </button>
      )}
      {open && (
        <div className="absolute z-30 mt-1 max-h-64 w-72 overflow-y-auto rounded-lg border border-white/10 bg-zinc-900 shadow-xl">
          {loading && (
            <div className="px-3 py-2 text-xs text-muted">Cargando…</div>
          )}
          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted">Sin resultados</div>
          )}
          {!loading &&
            options.map((opt) => (
              <button
                key={opt.client_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt);
                  setQuery("");
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-white/10"
              >
                {opt.client_name}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/* --- Selector de período --- */
function PeriodTabs({ value, onChange }) {
  const opts = [
    { v: "week", l: "Semana" },
    { v: "month", l: "Mes" },
    { v: "year", l: "Año" },
    { v: "custom", l: "Rango" },
  ];
  return (
    <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-1">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            value === o.v
              ? "bg-white/15 text-foreground"
              : "text-muted hover:text-foreground"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

/* --- KPI Card --- */
function KpiCard({ label, value, sub }) {
  return (
    <div className="card rounded-xl p-4 flex flex-col gap-1 border border-white/10">
      <span className="text-xs text-muted uppercase tracking-wide">{label}</span>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      {sub && <span className="text-xs text-muted">{sub}</span>}
    </div>
  );
}

/* --- Pill --- */
function Pill({ children, className = "bg-zinc-500/20 text-zinc-300" }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

/* --- Fila detalle (instrumentos de una oferta) --- */
function DetailRows({ offerId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error: err } = await supabase
        .from("v_client_report_detail")
        .select(
          "certificate_number,service_order_code,service_kind,tag,brand,model,serial,category_name,technical_status,technician_name,reception_date,calibration_date,departure_date,instrument_id,price,qty"
        )
        .eq("offer_id", offerId)
        .order("service_order_code", { ascending: true });
      if (!active) return;
      if (err) setError(err.message);
      setRows(data || []);
    })();
    return () => {
      active = false;
    };
  }, [offerId]);

  if (error) return <div className="px-4 py-3 text-xs text-rose-400">Error: {error}</div>;
  if (rows === null)
    return <div className="px-4 py-3 text-xs text-muted">Cargando instrumentos…</div>;
  if (rows.length === 0)
    return <div className="px-4 py-3 text-xs text-muted">Sin instrumentos asociados.</div>;

  return (
    <div className="bg-black/30">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted">
            <th className="py-2 pl-12 pr-3">Certif.</th>
            <th className="py-2 px-3">Servicio</th>
            <th className="py-2 px-3">Tipo</th>
            <th className="py-2 px-3">Marca</th>
            <th className="py-2 px-3">Modelo</th>
            <th className="py-2 px-3">N° Serie</th>
            <th className="py-2 px-3">TAG</th>
            <th className="py-2 px-3">Est. Técnico</th>
            <th className="py-2 px-3">Técnico</th>
            <th className="py-2 px-3">F. Ingreso</th>
            <th className="py-2 px-3">F. Calib.</th>
            <th className="py-2 px-3">F. Entrega</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.certificate_number} className="border-t border-white/5 hover:bg-white/5">
              <td className="py-2 pl-12 pr-3 font-mono text-sky-300">{r.certificate_number}</td>
              <td className="py-2 px-3">
                <Pill
                  className={
                    r.service_kind === "repair"
                      ? "bg-orange-500/20 text-orange-300"
                      : "bg-sky-500/20 text-sky-300"
                  }
                >
                  {r.service_kind === "repair" ? "Reparación" : "Calibración"}
                </Pill>
              </td>
              <td className="py-2 px-3">{r.category_name || r.tag || "—"}</td>
              <td className="py-2 px-3">{r.brand || "—"}</td>
              <td className="py-2 px-3">{r.model || "—"}</td>
              <td className="py-2 px-3">{r.serial || "—"}</td>
              <td className="py-2 px-3 font-mono">{r.tag || "—"}</td>
              <td className="py-2 px-3">
                <Pill className={TECH_STATUS_PILL[r.technical_status] || TECH_STATUS_PILL.Pendiente}>
                  {r.technical_status}
                </Pill>
              </td>
              <td className="py-2 px-3">{r.technician_name || "—"}</td>
              <td className="py-2 px-3">{fmtDate(r.reception_date)}</td>
              <td className="py-2 px-3">{fmtDate(r.calibration_date)}</td>
              <td className="py-2 px-3">{fmtDate(r.departure_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --- Página principal --- */
export default function ClientReportPage() {
  const [client, setClient] = useState(null);
  const [period, setPeriod] = useState("year");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [master, setMaster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const { from, to } = useMemo(
    () => computePeriodRange(period, customFrom, customTo),
    [period, customFrom, customTo]
  );

  /* -- Fetch master --
     - Con cliente seleccionado: trae sus ofertas (vista detallada).
     - Sin cliente seleccionado: trae todas las ofertas del período (vista agrupada por cliente). */
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setExpanded(new Set());
    let q = supabase
      .from("v_client_report_master")
      .select(
        "offer_id,offer_code,cost_center,client_id,client_name,status,oc_number,currency,subtotal,tax,total,quote_date,contact_name,invoice_number,invoiced_date,billing_requested_date,remito_numbers,first_remito_date,last_remito_date,instrument_count"
      )
      .order("quote_date", { ascending: false })
      .limit(5000);
    if (client) q = q.eq("client_id", client.client_id);
    if (from) q = q.gte("quote_date", from);
    if (to) q = q.lte("quote_date", to);
    const { data, error: err } = await q;
    if (err) setError(err.message);
    setMaster(data || []);
    setLoading(false);
  }, [client, from, to]);

  /* -- Agrupado por cliente (cuando no hay cliente seleccionado) -- */
  const clientsAggregate = useMemo(() => {
    if (client) return [];
    const map = new Map();
    for (const r of master) {
      if (!r.client_id) continue;
      let g = map.get(r.client_id);
      if (!g) {
        g = {
          client_id: r.client_id,
          client_name: r.client_name,
          offer_count: 0,
          instrument_count: 0,
          quotedByCurrency: {},
          invoicedByCurrency: {},
          remito_count: 0,
          last_activity: null,
        };
        map.set(r.client_id, g);
      }
      g.offer_count += 1;
      g.instrument_count += Number(r.instrument_count || 0);
      const cur = normalizeCurrency(r.currency);
      g.quotedByCurrency[cur] = (g.quotedByCurrency[cur] || 0) + Number(r.total || 0);
      if (r.invoice_number) {
        g.invoicedByCurrency[cur] = (g.invoicedByCurrency[cur] || 0) + Number(r.total || 0);
      }
      g.remito_count += Array.isArray(r.remito_numbers) ? r.remito_numbers.length : 0;
      if (!g.last_activity || (r.quote_date && r.quote_date > g.last_activity)) {
        g.last_activity = r.quote_date;
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const aTot = Object.values(a.quotedByCurrency).reduce((s, v) => s + v, 0);
      const bTot = Object.values(b.quotedByCurrency).reduce((s, v) => s + v, 0);
      return bTot - aTot;
    });
  }, [client, master]);

  useEffect(() => {
    load();
  }, [load]);

  /* -- KPIs agregados -- */
  const kpis = useMemo(() => {
    const k = {
      offerCount: master.length,
      instrumentCount: 0,
      quotedByCurrency: {},
      invoicedByCurrency: {},
      remitoCount: 0,
      conversion: 0,
    };
    let invoicedOffers = 0;
    for (const r of master) {
      k.instrumentCount += Number(r.instrument_count || 0);
      const cur = normalizeCurrency(r.currency);
      k.quotedByCurrency[cur] = (k.quotedByCurrency[cur] || 0) + Number(r.total || 0);
      if (r.invoice_number) {
        invoicedOffers++;
        k.invoicedByCurrency[cur] = (k.invoicedByCurrency[cur] || 0) + Number(r.total || 0);
      }
      k.remitoCount += Array.isArray(r.remito_numbers) ? r.remito_numbers.length : 0;
    }
    k.conversion = master.length ? (invoicedOffers / master.length) * 100 : 0;
    return k;
  }, [master]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* -- Export CSV -- */
  const exportCsv = () => {
    if (!master.length) return;
    const headers = [
      "Centro Costo",
      "Cliente",
      "Estado",
      "OC",
      "Moneda",
      "Total",
      "Fecha Cotiz.",
      "Contacto",
      "Remitos",
      "Factura",
      "Fecha FC",
      "# Instr.",
    ];
    const lines = [headers.join(";")];
    for (const r of master) {
      lines.push(
        [
          r.cost_center || "",
          r.client_name || "",
          STATUS_LABEL[r.status] || r.status || "",
          r.oc_number || "",
          r.currency || "",
          Number(r.total || 0).toFixed(2),
          fmtDate(r.quote_date),
          r.contact_name || "",
          (r.remito_numbers || []).join(" | "),
          r.invoice_number || "",
          fmtDate(r.invoiced_date),
          r.instrument_count || 0,
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(";")
      );
    }
    const blob = new Blob(["ï»¿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reporte_${client?.client_name || "cliente"}_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 space-y-4">
      {/* Encabezado */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-foreground">Reporte Cliente 360°</h1>
          <p className="text-xs text-muted">
            Vista consolidada de ofertas, cotizaciones, instrumentos, facturación y remitos.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!master.length}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Exportar CSV
        </button>
      </div>

      {/* Filtros */}
      <div className="card rounded-xl border border-white/10 p-3 flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted">Cliente</label>
          <ClientPicker value={client} onChange={setClient} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted">Período</label>
          <PeriodTabs value={period} onChange={setPeriod} />
        </div>
        {period === "custom" && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted">Desde</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted">Hasta</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
            </div>
          </>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          label={client ? "Ofertas" : "Clientes activos"}
          value={fmt(client ? kpis.offerCount : clientsAggregate.length)}
          sub={client ? undefined : `${fmt(kpis.offerCount)} ofertas`}
        />
        <KpiCard label="Instrumentos" value={fmt(kpis.instrumentCount)} />
        <KpiCard
          label="Cotizado"
          value={<CurrencyBreakdown values={kpis.quotedByCurrency} />}
        />
        <KpiCard
          label="Facturado"
          value={<CurrencyBreakdown values={kpis.invoicedByCurrency} />}
          sub={`Conversión: ${fmt(kpis.conversion, 1)}%`}
        />
        <KpiCard label="Remitos" value={fmt(kpis.remitoCount)} />
      </div>

      {/* Tabla principal */}
      <div className="card rounded-xl border border-white/10 overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-muted text-sm">Cargando…</div>
        )}
        {!loading && error && (
          <div className="p-8 text-center text-rose-400 text-sm">Error: {error}</div>
        )}
        {!loading && !error && master.length === 0 && (
          <div className="p-8 text-center text-muted text-sm">
            No hay actividad en el período seleccionado.
          </div>
        )}

        {/* MODO AGRUPADO: sin cliente seleccionado → 1 fila por cliente */}
        {!client && !loading && !error && clientsAggregate.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-muted">
                <th className="py-2 px-3">Cliente</th>
                <th className="py-2 px-3 text-right">Ofertas</th>
                <th className="py-2 px-3 text-right">Instrumentos</th>
                <th className="py-2 px-3">Cotizado</th>
                <th className="py-2 px-3">Facturado</th>
                <th className="py-2 px-3 text-right">Remitos</th>
                <th className="py-2 px-3">Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {clientsAggregate.map((g) => (
                <tr
                  key={g.client_id}
                  className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() =>
                    setClient({ client_id: g.client_id, client_name: g.client_name })
                  }
                  title="Click para ver el detalle de este cliente"
                >
                  <td className="py-2 px-3 font-semibold">{g.client_name}</td>
                  <td className="py-2 px-3 text-right">{fmt(g.offer_count)}</td>
                  <td className="py-2 px-3 text-right">{fmt(g.instrument_count)}</td>
                  <td className="py-2 px-3">
                    <CurrencyBreakdown values={g.quotedByCurrency} compact />
                  </td>
                  <td className="py-2 px-3">
                    <CurrencyBreakdown values={g.invoicedByCurrency} compact />
                  </td>
                  <td className="py-2 px-3 text-right">{fmt(g.remito_count)}</td>
                  <td className="py-2 px-3">{fmtDate(g.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* MODO DETALLE: con cliente seleccionado → 1 fila por oferta */}
        {client && !loading && !error && master.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-muted">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3">Centro Costo</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3">OC</th>
                <th className="py-2 px-3">Moneda</th>
                <th className="py-2 px-3">Total</th>
                <th className="py-2 px-3">F. Cotiz.</th>
                <th className="py-2 px-3">Contacto</th>
                <th className="py-2 px-3">Remito(s)</th>
                <th className="py-2 px-3">Factura</th>
                <th className="py-2 px-3">F. Factura</th>
                <th className="py-2 px-3 text-right"># Instr.</th>
              </tr>
            </thead>
            <tbody>
              {master.map((r) => {
                const isOpen = expanded.has(r.offer_id);
                return (
                  <React.Fragment key={r.offer_id}>
                    <tr
                      className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => toggleExpand(r.offer_id)}
                    >
                      <td className="py-2 px-3 text-muted">{isOpen ? "▼" : "▶"}</td>
                      <td className="py-2 px-3 font-semibold">{r.cost_center}</td>
                      <td className="py-2 px-3">
                        <Pill className={STATUS_PILL[r.status] || STATUS_PILL.draft}>
                          {STATUS_LABEL[r.status] || r.status}
                        </Pill>
                      </td>
                      <td className="py-2 px-3 font-mono">{r.oc_number || "—"}</td>
                      <td className="py-2 px-3">{r.currency || "—"}</td>
                      <td className="py-2 px-3 font-mono">{fmtMoney(r.total, r.currency)}</td>
                      <td className="py-2 px-3">{fmtDate(r.quote_date)}</td>
                      <td className="py-2 px-3">{r.contact_name || "—"}</td>
                      <td className="py-2 px-3 font-mono">
                        {(r.remito_numbers || []).join(", ") || "—"}
                      </td>
                      <td className="py-2 px-3 font-mono">{r.invoice_number || "—"}</td>
                      <td className="py-2 px-3">{fmtDate(r.invoiced_date)}</td>
                      <td className="py-2 px-3 text-right">{r.instrument_count}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={12} className="p-0">
                          <DetailRows offerId={r.offer_id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
